import React from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import { extractErrorMessage } from '../../utils/apitools'
import { skyJobURL } from '../../hooks/socialdash/skyEndpoints'
import type { EntityImportResult, EntityQuery } from '../../types/gotypes'

interface PostDataCsvActionsProps {
  /** The filter the list is showing, so the download matches the screen. */
  query: EntityQuery
}

/**
 * Download and Upload for Post data — the spreadsheet round trip (#1194, #1196).
 *
 * The server has had both halves since #1289, but nothing on screen called
 * them, so the recovery path for Sky's lost U35 figures was curl-only.
 *
 * 🔴 THE UPLOAD IS TWO STEPS, AND THE SECOND SENDS THE SAME FILE. The server's
 * dry run is structural, not a flag: a request with no token previews and writes
 * nothing, and only the token that preview returned applies — to those exact
 * bytes, within 30 minutes. So Apply exists only inside the preview, and it
 * re-sends the File object that was previewed rather than asking again.
 *
 * Admins only, like the page: the server refuses both doors to anyone else.
 */
export const PostDataCsvActions: React.FC<PostDataCsvActionsProps> = ({ query }) => {
  const queryClient = useQueryClient()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [downloading, setDownloading] = React.useState(false)
  const [file, setFile] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState<EntityImportResult | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [applied, setApplied] = React.useState<EntityImportResult | null>(null)
  const [error, setError] = React.useState('')

  const download = async () => {
    setDownloading(true)
    setError('')
    try {
      // An empty filter is OMITTED, not sent as `{}`: the whole job comes down.
      const hasFilter = Object.keys(query).length > 0
      const res = await axios.get(skyJobURL('/entities/export'), {
        params: hasFilter ? { entity: JSON.stringify(query) } : {},
        responseType: 'blob',
      })
      const disposition = String(res.headers?.['content-disposition'] ?? '')
      const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'posts.csv'
      const href = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = href
      a.download = name
      a.click()
      URL.revokeObjectURL(href)
    } catch (e) {
      setError(extractErrorMessage(e) || 'The download failed.')
    } finally {
      setDownloading(false)
    }
  }

  const send = (f: File, token?: string) => {
    const form = new FormData()
    form.append('file', f)
    if (token) form.append('token', token)
    return axios.post<EntityImportResult>(skyJobURL('/entities/import'), form)
  }

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    // Cleared so picking the same file again (after fixing it) fires again.
    e.target.value = ''
    if (!picked) return
    setFile(picked)
    setPreview(null)
    setApplied(null)
    setError('')
    setBusy(true)
    try {
      const res = await send(picked)
      setPreview(res.data)
    } catch (err) {
      setError(extractErrorMessage(err) || 'The preview failed.')
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    if (!file || !preview?.token) return
    setBusy(true)
    setError('')
    try {
      const res = await send(file, preview.token)
      setApplied(res.data)
      // The list and any open post must show the new values, not cached old ones.
      queryClient.invalidateQueries({ queryKey: ['sky_entity_search'] })
      queryClient.invalidateQueries({ queryKey: ['sky_entity_detail'] })
    } catch (err) {
      setError(extractErrorMessage(err) || 'The upload was not applied.')
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    setFile(null)
    setPreview(null)
    setApplied(null)
    setError('')
  }

  const hasErrors = Boolean(preview && (preview.columnErrors.length > 0 || preview.rowErrors.length > 0))
  const changes = preview ? preview.counts.add + preview.counts.change + preview.counts.delete : 0
  const canApply = Boolean(preview?.token) && !hasErrors && changes > 0 && !busy && !applied

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          color="info"
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={download}
          disabled={downloading}
          data-testid="post-data-csv-download"
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </Button>
        <Button
          color="info"
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="post-data-csv-upload"
        >
          Upload CSV
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,text/csv"
          hidden
          onChange={pick}
          data-testid="post-data-csv-file"
        />
      </Box>

      {error && !file && (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="post-data-csv-error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Dialog open={Boolean(file)} onClose={busy ? undefined : close} maxWidth="md" fullWidth>
        <DialogTitle>{applied ? 'Upload applied' : `Preview: ${file?.name ?? ''}`}</DialogTitle>
        <DialogContent dividers data-testid="post-data-csv-preview">
          {busy && !preview && <Typography>Reading the file…</Typography>}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="post-data-csv-error">
              {error}
            </Alert>
          )}

          {applied && (
            <Alert severity="success" sx={{ mb: 2 }} data-testid="post-data-csv-applied">
              Applied — {applied.entitiesUpdated} post{applied.entitiesUpdated === 1 ? '' : 's'} updated.
            </Alert>
          )}

          {preview && !applied && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography>
                {preview.totalRows} row{preview.totalRows === 1 ? '' : 's'}, {preview.matchedRows} matched to a post.
                Nothing has been written yet.
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {preview.counts.change} changed · {preview.counts.add} added · {preview.counts.delete} deleted ·{' '}
                {preview.counts.unchanged} unchanged
              </Typography>

              {preview.unmatchedEntityIds.length > 0 && (
                <Alert severity="warning">
                  {preview.unmatchedEntityIds.length} row{preview.unmatchedEntityIds.length === 1 ? '' : 's'} name
                  an entity_id that is not in this job and will be skipped: {preview.unmatchedEntityIds.slice(0, 10).join(', ')}
                  {preview.unmatchedEntityIds.length > 10 ? '…' : ''}
                </Alert>
              )}
              {preview.unknownColumns.length > 0 && (
                <Alert severity="warning">Ignored — not a column this page knows: {preview.unknownColumns.join(', ')}</Alert>
              )}
              {preview.readOnlyColumns.length > 0 && (
                <Alert severity="info">
                  Read-only, so edits here are ignored: {preview.readOnlyColumns.join(', ')}
                </Alert>
              )}
              {(preview.newMetricKeys.length > 0 || preview.newLabelKeys.length > 0) && (
                <Alert severity="info">
                  New to every matched post — check the spelling:{' '}
                  {[...preview.newMetricKeys.map((k) => `metric:${k}`), ...preview.newLabelKeys.map((k) => `label:${k}`)].join(', ')}
                </Alert>
              )}

              {hasErrors && (
                <Alert severity="error">
                  This file has errors and cannot be applied. Fix them and upload it again.
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {preview.columnErrors.map((c, i) => (
                      <li key={`c${i}`}>
                        Column <code>{c.column}</code>: {c.reason}
                      </li>
                    ))}
                    {preview.rowErrors.slice(0, 50).map((r, i) => (
                      <li key={`r${i}`}>
                        Row {r.row} ({r.entityId}){r.column ? `, ${r.column}` : ''}
                        {r.value !== undefined ? ` = "${r.value}"` : ''}: {r.reason}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              {/* Listed one by one, with the value about to go: a count alone
                  does not let anyone check WHICH values a blank cell deletes. */}
              {preview.deletions.length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 600, color: 'error.main' }}>
                    These values will be deleted (a blank cell deletes):
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {preview.deletions.map((d, i) => (
                      <li key={i} data-testid={`post-data-csv-deletion-${i}`}>
                        Row {d.row}, {d.entityId}, <code>{d.column}</code> — currently “{d.currentValue}”
                      </li>
                    ))}
                  </Box>
                  {preview.deletionsTruncated && (
                    <Typography variant="caption">
                      …and more. {preview.counts.delete} deletions in total.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} color="info" disabled={busy}>
            {applied ? 'Close' : 'Cancel'}
          </Button>
          {!applied && (
            <Button
              onClick={apply}
              color="info"
              variant="contained"
              disabled={!canApply}
              data-testid="post-data-csv-apply"
            >
              {busy && preview ? 'Applying…' : `Apply ${changes} change${changes === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PostDataCsvActions
